import type { AddressResult } from "@dransay/address-validation";
import * as Sentry from "@sentry/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebouncedCallback } from "./use-debounced-callback.hook";

interface AddressAutocompletionResponse {
	results?: AddressResult[];
	error?: string;
}

export interface UseAddressAutocompletionReturn {
	suggestions: AddressResult[];
	isLoading: boolean;
	error: string | null;
	search: (query: string) => void;
	clearResults: () => void;
}

export function useAddressSuggestions(
	options: {
		debounceDelay?: number;
		limit?: number;
		emptyMessageText?: string;
	} = {},
): UseAddressAutocompletionReturn {
	const { debounceDelay = 300, limit = 5, emptyMessageText } = options;
	const { t } = useTranslation("auth");

	const abortControllerRef = useRef<AbortController | null>(null);
	const [suggestions, setSuggestions] = useState<AddressResult[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Use plain fetch instead of useFetcher to handle 429 rate limiting gracefully.
	// When CloudFlare returns 429 responses, we want to show user-friendly error messages
	// rather than triggering the ErrorBoundary which would happen with useFetcher.
	const searchAddress = useCallback(
		async (query: string) => {
			if (!query.trim()) {
				setSuggestions([]);
				setError(null);
				return;
			}

			// Cancel previous request
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}

			const abortController = new AbortController();
			abortControllerRef.current = abortController;

			setIsLoading(true);
			setError(null);

			try {
				const params = new URLSearchParams({
					query,
					limit: limit.toString(),
				});

				const response = await fetch(`/api/address/autocompletion?${params}`, {
					signal: abortController.signal,
					credentials: "same-origin",
				});

				// Check if request was aborted
				if (abortController.signal.aborted) {
					return;
				}

				// Handle 429 rate limiting from CloudFlare
				if (response.status === 429) {
					setError(t("errors.tooManyAttemptsTryLater"));
					setSuggestions([]);
					return;
				}

				// Handle other HTTP error responses
				if (!response.ok) {
					setError(t("errors.address.failedToFetch"));
					setSuggestions([]);
					return;
				}

				const data: AddressAutocompletionResponse = await response.json();

				// Handle application-level errors from our route
				if (data.error) {
					setError(data.error);
					setSuggestions([]);
					return;
				}

				// Handle successful responses
				if (data.results) {
					setSuggestions(data.results);

					if (
						Array.isArray(data.results) &&
						data.results.length === 0 &&
						emptyMessageText
					) {
						setError(emptyMessageText);
					} else {
						setError(null);
					}
				}
			} catch (error) {
				// Don't handle aborted requests as errors
				if (error instanceof Error && error.name === "AbortError") {
					return;
				}

				Sentry.addBreadcrumb({
					category: "address-autocompletion",
					message: "Failed to fetch address autocompletion",
					data: { error },
				});
				Sentry.captureMessage("Failed to fetch address autocompletion");

				setError(t("errors.address.failedToFetch"));
				setSuggestions([]);
			} finally {
				setIsLoading(false);
				abortControllerRef.current = null;
			}
		},
		[limit, t, emptyMessageText],
	);

	const debouncedSearch = useDebouncedCallback(searchAddress, {
		delay: debounceDelay,
		leading: false,
	});

	const clearResults = useCallback(() => {
		setSuggestions([]);
		setError(null);
	}, []);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
		};
	}, []);

	return {
		suggestions,
		isLoading,
		error,
		search: debouncedSearch,
		clearResults,
	};
}
